import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { Phone, Crown, Footprints } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { IMAGES, PHONE_TEL } from "@/data/spa";
import { AmbientMedia } from "@/components/spa/AmbientMedia";
import { useAmbience } from "@/context/AmbienceContext";
import { TiltCard } from "@/components/spa/TiltCard";

const Ritual = ({ icon: Icon, data, image, video, char, delay, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    <TiltCard
      testid={testid}
      max={5}
      lift={8}
      glow="0 28px 70px -20px rgba(197,160,89,0.55)"
      className="group relative overflow-hidden border border-white/10 hover:border-[#c5a059]/50 bg-[#0c100e]"
    >
    <div className="relative h-72 overflow-hidden">
      <AmbientMedia
        video={video}
        poster={image}
        alt={data.title}
        className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-108"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0c100e] via-[#0c100e]/30 to-transparent" />
      <span aria-hidden="true" className="absolute top-4 right-5 font-serif-display text-6xl text-[#c5a059]/25 select-none">{char}</span>
      <div className="absolute bottom-5 left-6 flex items-center gap-3">
        <Icon size={18} className="text-[#c5a059]" />
        <h3 className="font-serif-display text-3xl text-[#f0f4f2]">{data.title}</h3>
      </div>
    </div>
    <div className="bg-[#0c100e] p-7">
      <p className="text-sm text-[#8a9690] font-light leading-relaxed">{data.text}</p>
      <div className="mt-6 space-y-0">
        {data.steps.map((s, i) => (
          <div key={i} className="flex items-center gap-4 py-2.5 border-t border-white/5">
            <span className="font-serif-display italic text-[#c5a059] text-sm">0{i + 1}</span>
            <span className="text-sm text-[#f0f4f2]/80 font-light">{s}</span>
          </div>
        ))}
      </div>
    </div>
    </TiltCard>
  </motion.div>
);

export const SignatureRituals = () => {
  const { t } = useLang();
  const r = t.rituals;
  const ref = useRef(null);
  const { setTreatmentIntensity } = useAmbience();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  // Smooth ramp: 0 -> 1 as section enters center, 1 -> 0 as it leaves
  const treatment = useTransform(scrollYProgress, [0, 0.25, 0.75, 1], [0, 1, 1, 0]);
  useMotionValueEvent(treatment, "change", (v) => setTreatmentIntensity(v));
  useEffect(() => () => setTreatmentIntensity(0), [setTreatmentIntensity]);

  return (
    <section ref={ref} id="rituals" data-testid="rituals-section" className="relative py-24 lg:py-40 bg-[#0c100e] overflow-hidden">
      <span aria-hidden="true" className="ghost-char top-10 right-4">沐</span>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-[#c5a059]" />
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#c5a059]">{r.label}</span>
          </div>
          <h2 className="font-serif-display font-medium text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#f0f4f2]">
            {r.title.pre}<span className="italic text-[#c5a059]">{r.title.it}</span>{r.title.post}
          </h2>
          <p className="mt-6 text-[#8a9690] font-light leading-relaxed max-w-lg">{r.intro}</p>
        </motion.div>

        <div className="mt-16 grid md:grid-cols-2 gap-8">
          <Ritual icon={Crown} data={r.head} image={IMAGES.headMassage} video="/rituals/v7.mp4" char="首" delay={0} testid="ritual-head" />
          <Ritual icon={Footprints} data={r.foot} image={IMAGES.footMassage} video="/rituals/v10.mp4" char="足" delay={0.15} testid="ritual-foot" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10"
        >
          <TiltCard
            testid="ladies-promo-banner"
            max={3}
            lift={5}
            glow="0 24px 60px -18px rgba(197,160,89,0.6)"
            className="relative overflow-hidden bg-[#c5a059] text-[#050706] px-8 py-8 lg:px-12 flex flex-col lg:flex-row lg:items-center gap-6"
          >
            <span aria-hidden="true" className="absolute -right-4 -top-10 font-serif-display text-[10rem] leading-none text-[#050706]/10 select-none">婉</span>
            <div className="flex-1 relative">
              <span className="text-[10px] tracking-[0.35em] uppercase font-semibold">{r.ladies.tag}</span>
              <h3 className="mt-2 font-serif-display text-3xl lg:text-4xl font-medium">{r.ladies.title}</h3>
              <p className="mt-2 text-sm font-light max-w-xl">{r.ladies.text}</p>
            </div>
            <a
              href={PHONE_TEL}
              data-testid="ladies-promo-call"
              className="relative shrink-0 inline-flex items-center gap-3 bg-[#050706] text-[#c5a059] hover:text-[#dcc286] px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium transition-colors duration-300"
            >
              <Phone size={14} /> {r.ladies.cta}
            </a>
          </TiltCard>
        </motion.div>
      </div>
    </section>
  );
};
